import { useEffect, useState } from 'react';
import { getEntitlements } from '@surfline/web-common';

import { adTagConfig } from 'components/CamPlayer/camConfig';
import { getConditionsBasedAdUnits } from 'selectors/spot';
import { useUserDetails } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import getUserType from 'utils/getUserType';
import getUsPrivacyString from 'utils/getUsPrivacyString';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';

const GAM_VAST_BASE = 'https://pubads.g.doubleclick.net/gampad/ads';

const buildCustParams = (
  camId: string | undefined,
  userType: string | undefined,
  conditionsBasedAdUnits:
    | {
        pub_meta_2?: string;
        pub_meta_3?: string;
        pub_meta_5?: string;
        pub_meta_8?: string;
        pub_meta_9?: string;
      }
    | undefined,
): string => {
  const params: Record<string, string> = {};
  if (camId) params.Cam_ID = camId;
  if (userType) params.User_Type = userType;
  if (conditionsBasedAdUnits) {
    if (conditionsBasedAdUnits.pub_meta_2) {
      params.Air_temperature = String(conditionsBasedAdUnits.pub_meta_2);
    }
    if (conditionsBasedAdUnits.pub_meta_3) {
      params.Wind = String(conditionsBasedAdUnits.pub_meta_3);
    }
    if (conditionsBasedAdUnits.pub_meta_5) {
      params.Water_Temp = String(conditionsBasedAdUnits.pub_meta_5);
    }
    if (conditionsBasedAdUnits.pub_meta_8) {
      params.Surf_Height = String(conditionsBasedAdUnits.pub_meta_8);
    }
    if (conditionsBasedAdUnits.pub_meta_9) {
      params.Surf_Conditions_Rating = String(conditionsBasedAdUnits.pub_meta_9);
    }
  }
  return Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
};

const FREESTAR_VIDEO_AD_UNIT = '/1024858/ca-video-pub-4628350808233105-tag/6190335989';

const buildGamVastUrl = (usPrivacy: string, custParams: string): string => {
  const params = new URLSearchParams({
    iu: FREESTAR_VIDEO_AD_UNIT,
    sz: adTagConfig.sz,
    ciu_szs: adTagConfig.ciu_szs,
    impl: adTagConfig.impl,
    gdfp_req: adTagConfig.gdfp_req,
    env: adTagConfig.env,
    output: adTagConfig.output,
    unviewed_position_start: adTagConfig.unviewed_position_start,
    tfcd: '0',
    npa: '0',
    correlator: String(Date.now()),
    url: `${window.location.protocol}//${window.location.hostname}`,
    description_url: window.location.href,
    us_privacy: usPrivacy,
    cust_params: custParams,
  });
  return `${GAM_VAST_BASE}?${params.toString()}`;
};

const useFreestarAdVastTag = (hasAdFreeCamPermissions: boolean, camId?: string, enabled = true) => {
  const [adVastUrl, setAdVastUrl] = useState('');
  const entitlements = useAppSelector(getEntitlements);
  const conditionsBasedAdUnits = useAppSelector(getConditionsBasedAdUnits);
  const userDetails = useUserDetails();
  const isRegisteredUser = !!userDetails?._id;

  useEffect(() => {
    if (!enabled || hasAdFreeCamPermissions) {
      setAdVastUrl('');
      return undefined;
    }
    let cancelled = false;
    try {
      getUsPrivacyString()
        .then((usPrivacy) => {
          if (cancelled) return;
          try {
            const userType = getUserType(entitlements, isRegisteredUser);
            const custParams = buildCustParams(camId, userType, conditionsBasedAdUnits);
            setAdVastUrl(buildGamVastUrl(usPrivacy, custParams));
          } catch (error) {
            reportErrorToNewRelic(error as Error, 'useFreestarAdVastTag', {
              stage: 'buildUrl',
            });
            setAdVastUrl('');
          }
        })
        .catch((error) => {
          if (cancelled) return;
          reportErrorToNewRelic(error as Error, 'useFreestarAdVastTag', {
            stage: 'getUsPrivacyString',
          });
          setAdVastUrl('');
        });
    } catch (error) {
      reportErrorToNewRelic(error as Error, 'useFreestarAdVastTag', {
        stage: 'effect',
      });
      setAdVastUrl('');
    }
    return () => {
      cancelled = true;
    };
  }, [
    hasAdFreeCamPermissions,
    enabled,
    camId,
    entitlements,
    isRegisteredUser,
    conditionsBasedAdUnits,
  ]);

  return { adVastUrl, adTagLoading: false };
};

export default useFreestarAdVastTag;
